import { AvatarConfig, NaturalDownmixConfig } from "../avatar/common/player/type";
import { ResourceType } from "../base/loader/type";
import { SmartScenarioBuilders } from "../avatar/common/player/util";
import { defaultConfig } from "./default.config";
import type { CameraConfig } from '../base/camera/type';
import type { LightingConfig } from '../base/light/type';
import type { RendererConfig } from '../base/renderer/type';
import type { LoaderConfig } from '../base/loader/type';
import type { ScenarioConfigs } from '../avatar/common/animation/type';

// Bobbi 리소스 설정
// 모델은 전용 VRM(GoodBobbi20.vrm), 애니메이션은 공용 vroid .vrma 재사용
export const bobbiResources: ResourceType[] = [
  { name: "bobbi", type: "model_only", path: "/vrm/GoodBobbi20.vrm", format: "vrm" },
  { name: "greeting_entrance01", type: "animation_only", path: "/vrma/vroid_girl@greeting_entrance01.vrma", format: "vrma" },
  { name: "idle_standby01", type: "animation_only", path: "/vrma/vroid_girl@idle_standby01.vrma", format: "vrma" },
  { name: "idle_standby02", type: "animation_only", path: "/vrma/vroid_girl@idle_standby02.vrma", format: "vrma" },
  { name: "input_listening01", type: "animation_only", path: "/vrma/vroid_girl@input_listening01.vrma", format: "vrma" },
  { name: "input_listening02", type: "animation_only", path: "/vrma/vroid_girl@input_listening02.vrma", format: "vrma" },
  { name: "input_listening03", type: "animation_only", path: "/vrma/vroid_girl@input_listening03.vrma", format: "vrma" },
  { name: "input_processing01", type: "animation_only", path: "/vrma/vroid_girl@input_processing01.vrma", format: "vrma" },
  { name: "response_speaking01", type: "animation_only", path: "/vrma/vroid_girl@response_speaking01.vrma", format: "vrma" },
  { name: "response_speaking02", type: "animation_only", path: "/vrma/vroid_girl@response_speaking02.vrma", format: "vrma" },
  { name: "response_speaking03", type: "animation_only", path: "/vrma/vroid_girl@response_speaking03.vrma", format: "vrma" },
  { name: "response_speaking04", type: "animation_only", path: "/vrma/vroid_girl@response_speaking04.vrma", format: "vrma" },
  { name: "response_speaking05", type: "animation_only", path: "/vrma/vroid_girl@response_speaking05.vrma", format: "vrma" }
];

/**
 * Bobbi 시나리오 설정 - 스마트 빌더 사용
 */
const BOBBI_SCENARIOS: ScenarioConfigs<string> = {
  entrance: SmartScenarioBuilders.auto("greeting_entrance", bobbiResources, {
    preferredMode: 'once',
    description: "입장 애니메이션",
    nextScenario: 'speaking'
  }),

  toss: SmartScenarioBuilders.auto("idle_standby", bobbiResources, {
    preferredMode: 'loop',
    description: "결제 대기 상태"
  }),

  processing: SmartScenarioBuilders.auto("input_processing", bobbiResources, {
    preferredMode: 'last_loop',
    description: "입력 처리 중 상태"
  }),

  listening: SmartScenarioBuilders.auto("input_listening", bobbiResources, {
    preferredMode: 'last_loop',
    description: "사용자 입력을 듣는 상태"
  }),

  speaking: SmartScenarioBuilders.auto("response_speaking", bobbiResources, {
    preferredMode: 'random',
    description: "말하기 상태"
  }),
};

// 카메라 설정
const bobbiCameraConfig: CameraConfig = {
  ...defaultConfig.camera,
  cameras: [
    {
      name: "bobbi_main_camera",
      type: "PerspectiveCamera",
      active: true,
      // 망원(fov 10)으로 원근 왜곡 최소화.
      // Bobbi는 구형 체형(폭≈키)이라 키가 아닌 "폭" 기준으로 날나니와 동일하게 프레이밍:
      // 모바일(390x844)에서 폭 86.7%, 좌우 중앙 — 날나니 실측치와 일치.
      // y는 세로 정중앙 배치(위 26.9% / 아래 26.7% 여백). y를 낮추면 아바타가 위로 올라감.
      fov: 10,
      position: { x: -0.06, y: 1.0, z: 25.0 },
      rotation: {
        x: 0,
        y: 0,
        z: 0
      },
      near: 0.1,
      far: 200
    }
  ]
};

// 조명 설정 - 최소한의 조명만 설정
const bobbiLightConfig: LightingConfig = {
  ...defaultConfig.lighting,
};

// 렌더러 설정 - bloom 없음
const bobbiRendererConfig: RendererConfig = {
  ...defaultConfig.renderer,
  bloom: undefined
};

// 로더 설정
const bobbiLoaderConfig: LoaderConfig = {
  ...defaultConfig.loader,
  options: {
    ...defaultConfig.loader.options,
    isVRM: true,
  }
};

// Bobbi 20 립싱크 — v18m 자연스러움 파이프라인 + 2카드 다운믹스
// GoodBobbi20.vrm 은 입 카드가 Cube 메시의 [1]=JawOpen_neutral, [2]=mouthClose_basis
// 둘뿐인 카드 리그. 52채널 ARKit 데이터를 crisp/립스무딩/라운딩 보정 후 이 두 장으로
// 접는다 (kemix-engine blendshape-player-natural.html 뷰어의 mouthDownmix 포팅).
const bobbiLipsyncConfig: NaturalDownmixConfig = {
  targets: {
    mouthMeshName: 'Cube',
    jawMorphIndex: 1,      // JawOpen_neutral
    closeMorphIndex: 2,    // mouthClose_basis
    neutralMorphIndex: 0,  // Neutral
    blinkMeshName: 'Circle.002',
    blinkMorphIndices: [2, 3], // eyeBlinkLeft / eyeBlinkRight (뷰어와 동일 경로)
  },
  // 뷰어의 "Good Bobbi 20 (Neutral 켬)" 변형 — Neutral 카드 상시 1
  pinNeutral: true,
  // 자동 깜빡임 — 기본 8 BPM은 발화당 0~1회 수준이라 15 BPM으로 상향 (대기 중엔 엔진 유휴 깜빡임)
  blinkEnabled: true,
  params: { blinkRate: 15 },
  normPriors: { jaw: 0.25, round: 0.35, spread: 0.25, close: 0.22, totMouth: 1.5 },
};

/**
 * Bobbi 아바타 설정
 */
export const bobbiAvatarConfig: AvatarConfig = {
  type: 'bobbi' as const,
  modelKey: 'bobbi',
  resources: bobbiResources,
  scenarioConfigs: BOBBI_SCENARIOS,
  // VRM1.0 + 로더의 rotateVRM0로 이미 +Z 정면 → 추가 회전 불필요 (180이면 뒤돌아버림)
  rotation: {
    x: 0,
    y: 0,
    z: 0
  },
  lipsync: {
    processingType: 'natural_downmix',
    config: bobbiLipsyncConfig,
  },
};

// Bobbi 통합 설정 인터페이스
export interface BobbiConfig {
  loader: LoaderConfig;
  camera: CameraConfig;
  lighting: LightingConfig;
  renderer: RendererConfig;
  avatar: AvatarConfig;
}

/**
 * Bobbi 설정 통합 객체
 */
export const bobbiConfig: BobbiConfig = {
  loader: bobbiLoaderConfig,
  camera: bobbiCameraConfig,
  lighting: bobbiLightConfig,
  renderer: bobbiRendererConfig,
  avatar: bobbiAvatarConfig
};
